import { useState, useMemo, useEffect, useCallback, useRef, type ReactNode } from "react";
import { Shield, Key, Lock, Activity, TrendingDown, TrendingUp, ShieldCheck, ChevronUp, SlidersHorizontal, UserCheck, UserX, Flame, ShieldAlert, Moon, Cloud, ScanSearch, Plus, Trash2 } from "lucide-react";
import { SiAmazon, SiKubernetes } from "react-icons/si";
import { Badge } from "../../ui/badge";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";

import { Tabs, TabsContent } from "../../ui/tabs";
import { getAWSCloudIAMRole, getAWSCloudOIDC, getAWSCloudSecrets, getAWSKubernetesSecrets, getAWSKubernetesServiceAccounts, getAWSKubernetesTokens, getAzureCloudSecrets, getAzureCloudServiceAccounts, getAzureCloudTokens, getAzureKubernetesSecrets, getAzureKubernetesServiceAccounts, getAzureKubernetesTokens, getGCPCloudSecrets, getGCPCloudServiceAccounts, getGCPCloudTokens, getGCPKubernetesSecrets, getGCPKubernetesServiceAccounts, getGCPKubernetesTokens } from "./Data";
import { DetailsDialog } from "../Cloud/DetailsDialog";
import { NhiOwnerSelector, NhiFrequentUsers, NhiDeleteButton, getNhiIdentifier } from "./NhiGovernance";
import { RiskTagBadge, TotalAlertsBadge } from "../Cloud/RiskTagBadge";
import { myLocalStorage } from "../../StorageHelper";
import { useNavigate, useSearchParams } from "react-router-dom";
import { VscAzure } from "react-icons/vsc";
import { FaGoogle } from "react-icons/fa6";
import useCloudSyncStore from "../../../services/useCloudSyncStore";
import useCloudSyncWatcher from "../../../services/useCloudSyncWatcher";
import { triggerCloudSync } from "../../../triggerCloudSync";
import CloudFilters from "../CloudFilters";
import AlertTrendPanel from "../Cloud/AlertTrendPanel";
import { SecurityProgressSkeleton, SummaryCardsSkeleton } from "../Cloud/CloudDashboardSkeletons";
import axios from "axios";
import { NGROK } from "../../../APIs";
import EmptyStateCard from "../EmptyStateCard";
import { getCloudAccountSelection } from "../cloudAccountSelection";
import { useQuery } from "react-query";
import {
    fetchSecretFootprintCounts,
    getSecretDiscoveryLocationCount,
} from "../CloudAnalytics/secretDiscoveryService";
import {
    SECRET_DISCOVERY_COUNTS_KEY,
    SecretDiscoveryScanPanel,
    StartSecretScanButton,
    useSecretDiscoveryScan,
} from "./SecretDiscoveryScanPanel";
import AddAdminSecretDialog from "./AddAdminSecretDialog";


const PLATFORM_TABS = ["cloud", "kubernetes"] as const;
const ENTITY_TABS = ["service-accounts", "secrets", "tokens"] as const;

function readTabParam(value: string | null, allowed: readonly string[], fallback: string): string {
    return value && allowed.includes(value) ? value : fallback;
}

// Activity gradient color based on count
const getActivityColor = (count: number) => {
    if (count < 10) return "bg-gradient-to-r from-yellow-400 to-yellow-500";
    if (count <= 50) return "bg-gradient-to-r from-orange-400 to-orange-500";
    return "bg-gradient-to-r from-orange-600 to-orange-700";
};

const getActivityIntensity = (count: number) => {
    if (count < 10) return Math.min((count / 10) * 100, 100);
    if (count <= 50) return Math.min(((count - 10) / 40) * 100, 100);
    return 100;
};

// Activity Bar Component
function ActivityBar({ count, className = "" }: { count: number; className?: string }) {
    const intensity = getActivityIntensity(count);
    const colorClass = getActivityColor(count);

    return (
        <div className={`relative h-2 bg-muted rounded-full overflow-hidden ${className}`}>
            <div
                className={`h-full ${colorClass} rounded-full transition-all`}
                style={{ width: `${intensity}%` }}
            />
        </div>
    );
}

const getDisplayValue = (value: any, fallback = "N/A") => {
    if (value === null || value === undefined || value === "") return fallback;
    return String(value);
};

const isUserManagedValue = (value: any) => {
    const val = String(value || "").toLowerCase();
    return val.includes("user managed") || val === "iam role";
};

const isAdminManagedValue = (value: any) => String(value || "").toLowerCase().includes("admin managed");

// Google-managed service agents are provisioned and owned by Google, so their owner is fixed
// and must not be re-assignable from the UI.
const GOOGLE_CLOUD_OWNER = "Google Cloud";
const isGoogleManagedServiceAccount = (cloudProvider: string, sa: any) =>
    cloudProvider === "gcp" && !isUserManagedValue(sa?.managedType);

const getManagedBadgeClass = (value: any) => {
    const base = "text-xs h-5";
    if (isAdminManagedValue(value)) {
        return `${base} border-violet-600 bg-violet-600 text-white hover:bg-violet-600 dark:border-violet-500 dark:bg-violet-600 dark:text-white`;
    }
    if (isUserManagedValue(value)) {
        return `${base} bg-primary text-primary-foreground border-primary hover:bg-primary/90 dark:bg-primary dark:text-primary-foreground dark:border-primary`;
    }
    return base;
};

const getDormancyBadgeClass = (isDormant: boolean) =>
    `text-xs h-5 ${isDormant
        ? "bg-red-500 text-white border-red-500 dark:bg-red-600 dark:text-white dark:border-red-600"
        : "bg-primary text-primary-foreground border-primary dark:bg-primary dark:text-primary-foreground dark:border-primary"
    }`;

type GovernanceFilterKey = "withOwner" | "withoutOwner" | "mostRisky" | "overPrivileged" | "mostActive" | "dormant" | "adminManaged";

type GovernanceFilterState = {
    owner: "withOwner" | "withoutOwner" | null;
    activity: "mostActive" | "dormant" | null;
    mostRisky: boolean;
    overPrivileged: boolean;
    adminManaged: boolean;
};

const getGovernanceField = (entity: any, field: string) => {
    const sources = [entity, entity?.secretDetails, entity?.cloudSecretDetails, entity?.k8Secrets];
    for (const source of sources) {
        if (source?.[field] !== undefined && source?.[field] !== null) return source[field];
    }
    return undefined;
};

const applyGovernanceFilters = (entities: any[], filters: GovernanceFilterState) => {
    let list = entities;
    const hasOwner = (entity: any) => {
        const owners = getGovernanceField(entity, "owners");
        return Array.isArray(owners) ? owners.length > 0 : Boolean(owners);
    };
    const numericField = (entity: any, ...fields: string[]) => {
        for (const field of fields) {
            const value = Number(getGovernanceField(entity, field));
            if (Number.isFinite(value)) return value;
        }
        return 0;
    };

    if (filters.owner === "withOwner") {
        list = list.filter(hasOwner);
    } else if (filters.owner === "withoutOwner") {
        list = list.filter((entity) => !hasOwner(entity));
    }
    if (filters.activity === "dormant") {
        list = list.filter((entity) => {
            const isDormant = getGovernanceField(entity, "isDormant");
            return isDormant === true || (isDormant === undefined && numericField(entity, "activity") === 0);
        });
    }
    if (filters.adminManaged) {
        list = list.filter((entity) => getGovernanceField(entity, "isAdminManaged") === true);
    }
    if (filters.mostRisky || filters.overPrivileged || filters.activity === "mostActive") {
        list = [...list].sort((a, b) => {
            const sorters = [
                filters.mostRisky ? numericField(b, "alertCount") - numericField(a, "alertCount") : 0,
                filters.overPrivileged
                    ? numericField(b, "highPrivilegeRoleCount", "highPrivilegePermissionCount", "overPrivilegedPermissionCount")
                    - numericField(a, "highPrivilegeRoleCount", "highPrivilegePermissionCount", "overPrivilegedPermissionCount")
                    : 0,
                filters.activity === "mostActive" ? numericField(b, "activity") - numericField(a, "activity") : 0,
            ];
            return sorters.find((delta) => delta !== 0) || 0;
        });
    }
    return list;
};

function GovernanceFilterPanel({
    entityLabel,
    hasFilter,
    isActive,
    onToggle,
    onClear,
    showAdminManaged = false,
    hideSecretUnsupportedFilters = false,
    action,
}: {
    entityLabel: string;
    hasFilter: boolean;
    isActive: (key: GovernanceFilterKey) => boolean;
    onToggle: (key: GovernanceFilterKey) => void;
    onClear: () => void;
    showAdminManaged?: boolean;
    hideSecretUnsupportedFilters?: boolean;
    action?: ReactNode;
}) {
    let filterItems: Array<{ key: GovernanceFilterKey; label: string; Icon: typeof UserX }> = [
        { key: "withoutOwner", label: "Without Owner", Icon: UserX },
        { key: "withOwner", label: "With Owner", Icon: UserCheck },
        { key: "mostRisky", label: "Most Risky", Icon: Flame },
        { key: "overPrivileged", label: "Over-privileged", Icon: ShieldAlert },
        { key: "mostActive", label: "Most Active", Icon: Activity },
        { key: "dormant", label: "Dormant", Icon: Moon },
    ];
    if (hideSecretUnsupportedFilters) {
        filterItems = filterItems.filter(({ key }) =>
            key !== "mostRisky" && key !== "overPrivileged" && key !== "dormant"
        );
    }
    if (showAdminManaged) {
        filterItems.push({ key: "adminManaged", label: "Admin Managed", Icon: ShieldCheck });
    }

    return (
        <div className="cloud-card-surface rounded-2xl border border-border/60 bg-gradient-to-br from-card via-card to-primary/5 p-3.5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                    <div className="mb-3 flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                            <SlidersHorizontal className="w-4 h-4" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-sm font-semibold leading-tight">Governance Filters</span>
                            <span className="text-[11px] text-muted-foreground leading-tight">
                                Refine {entityLabel} by {hideSecretUnsupportedFilters ? "ownership and activity" : "ownership, risk and activity"}
                            </span>
                        </div>
                        {hasFilter && (
                            <button
                                onClick={onClear}
                                className="ml-auto text-[11px] font-semibold text-muted-foreground hover:text-foreground rounded-full px-2.5 py-1 hover:bg-muted/60 transition-colors"
                            >
                                Clear filter
                            </button>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2.5">
                        {filterItems.map(({ key, label, Icon }) => (
                            <button
                                key={key}
                                onClick={() => onToggle(key)}
                                className={`cs-tab !rounded-full !px-3.5 !py-1.5 !text-[13px] ${isActive(key) ? "active" : ""}`}
                            >
                                <Icon className="w-4 h-4" />
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
                {action && (
                    <div className="flex flex-shrink-0 justify-end sm:self-center">
                    {action}
                    </div>
                )}
            </div>
        </div>
    );
}

const renderSecretCardMeta = (secret: any) => {
    // Owners come from the resource-audit attribution sweep as a list; never fall back to the
    // project/tenant name, which reads like a real owner but is not one.
    const ownersValue = getGovernanceField(secret, "owners");
    const owners: string[] = Array.isArray(ownersValue)
        ? ownersValue.filter(Boolean)
        : ownersValue
            ? [String(ownersValue)]
            : [];
    const createdAt = getDisplayValue(secret.createdAt);
    const replication = getDisplayValue(secret.replicationType);

    return (
        <div className="flex flex-col items-start gap-2">
            <div className="flex flex-wrap items-center gap-1.5">
                <div className="text-[11px] font-medium text-muted-foreground mr-1">Owner :</div>
                {owners.length > 0 ? (
                    owners.map((owner) => (
                        <Badge
                            key={owner}
                            variant="secondary"
                            className="text-[10px] h-5 bg-primary/10 text-primary border-primary/20"
                        >
                            {owner}
                        </Badge>
                    ))
                ) : (
                    <span className="text-[11px] text-muted-foreground font-medium">No owner assigned</span>
                )}
            </div>
            <div className="flex items-center gap-1.5">
                <div className="text-[11px] font-medium text-muted-foreground mr-1">Created :</div>
                <Badge variant="secondary" className="text-[10px] h-5 bg-muted/50">{createdAt}</Badge>
            </div>
            <div className="flex items-center gap-1.5">
                <div className="text-[11px] font-medium text-muted-foreground mr-1">Replication :</div>
                <Badge variant="secondary" className="text-[10px] h-5 bg-muted/50">{replication}</Badge>
            </div>
        </div>
    );
};

const CloudIcon = ({ provider }: { provider: string }) => {
    if (provider === 'gcp') {
        return <FaGoogle className="w-8 h-8 text-primary" />;
    } else if (provider === 'aws') {
        return <SiAmazon className="w-8 h-8 text-primary" />;
    } else {
        return <VscAzure className="w-8 h-8 text-primary" />;
    }
};


const filterRiskTags = (tags: string[]): string[] => {
    return tags.filter(t => !/^[+-]?\d+\s*alerts?$/i.test(t.trim()));
};

interface CloudNHIPageProps {
    cloudProvider: string;
}

export default function CloudNHIPage({ cloudProvider }: CloudNHIPageProps) {
    const navigate = useNavigate();
    const [selectedTenant, setSelectedTenant] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const [platformTab, setPlatformTab] = useState(
        () => readTabParam(searchParams.get("platform"), PLATFORM_TABS, "cloud"),
    );
    const [activeTab, setActiveTab] = useState(
        () => readTabParam(searchParams.get("tab"), ENTITY_TABS, "service-accounts"),
    );

    useEffect(() => {
        setSearchParams(
            (previous) => {
                const next = new URLSearchParams(previous);
                next.set("platform", platformTab);
                next.set("tab", activeTab);
                return next;
            },
            { replace: true },
        );
    }, [platformTab, activeTab, setSearchParams]);
    const [riskFilter, setRiskFilter] = useState<"all" | "improved" | "worsened" | "neutral">("all");
    const [govFilters, setGovFilters] = useState<GovernanceFilterState>({
        owner: null,
        activity: null,
        mostRisky: false,
        overPrivileged: false,
        adminManaged: false,
    });
    const [isAddAdminSecretOpen, setIsAddAdminSecretOpen] = useState(false);
    const latestTenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    const [securityProgress, setSecurityProgress] = useState<any>(null);
    const [securityProgressLoading, setSecurityProgressLoading] = useState(true);
    // Skeleton only on first load; background refreshes update in place so the panels above the
    // list don't flip to skeleton and shift the list under the cursor (caused dropped clicks).
    const securityProgressLoadedRef = useRef(false);
    const [hasMultipleSnapshots, setHasMultipleSnapshots] = useState(false);
    const [expandedRoles, setExpandedRoles] = useState<Record<string, boolean>>({});

    const hasGovernanceFilter =
        !!govFilters.owner || !!govFilters.activity || govFilters.mostRisky || govFilters.overPrivileged || govFilters.adminManaged;
    const hasSecretGovernanceFilter =
        !!govFilters.owner || govFilters.activity === "mostActive" || govFilters.adminManaged;

    const clearGovernanceFilters = () => {
        setGovFilters({
            owner: null,
            activity: null,
            mostRisky: false,
            overPrivileged: false,
            adminManaged: false,
        });
    };

    const toggleGovernanceFilter = (
        key: GovernanceFilterKey
    ) => {
        setGovFilters((prev) => {
            if (key === "withOwner" || key === "withoutOwner") {
                return { ...prev, owner: prev.owner === key ? null : key };
            }
            if (key === "mostActive" || key === "dormant") {
                return { ...prev, activity: prev.activity === key ? null : key };
            }
            return { ...prev, [key]: !prev[key] };
        });
    };

    const isGovernanceFilterActive = (
        key: GovernanceFilterKey
    ) => {
        if (key === "withOwner" || key === "withoutOwner") return govFilters.owner === key;
        if (key === "mostActive" || key === "dormant") return govFilters.activity === key;
        return govFilters[key];
    };

    const toggleRoles = (id: string) => {
        setExpandedRoles((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const [cloudServiceAccounts, setCloudServiceAccounts] = useState<any[]>([]);
    const [cloudTokens, setCloudTokens] = useState<any[]>([]);
    const [cloudSecrets, setCloudSecrets] = useState<any[]>([]);
    const [kubernetesServiceAccounts, setKubernetesServiceAccounts] = useState<any[]>([]);
    const [kubernetesTokens, setKubernetesTokens] = useState<any[]>([]);
    const [kubernetesSecrets, setKubernetesSecrets] = useState<any[]>([]);
    const [cloudProviderChanges, setCloudProviderChanges] = useState<any>();
    const [loading, setLoading] = useState(false);
    const { syncStatus } = useCloudSyncStore((state) => ({
        syncStatus: state.syncStatus[cloudProvider],
    }));
    const accountSelection = getCloudAccountSelection(cloudProvider);
    const hasCloudAccount = accountSelection.configured;

    const secretScanController = useSecretDiscoveryScan({
        cloudProvider,
        tenantName: latestTenantName,
        projectId: accountSelection.id,
    });
    const { data: secretFootprintCounts } = useQuery(
        [SECRET_DISCOVERY_COUNTS_KEY, cloudProvider, latestTenantName, accountSelection.id],
        () => fetchSecretFootprintCounts(cloudProvider, latestTenantName, accountSelection.id),
        {
            enabled: secretScanController.supported,
            refetchOnWindowFocus: false,
        },
    );

    const clearNhiData = useCallback(() => {
        setCloudServiceAccounts([]);
        setCloudTokens([]);
        setCloudSecrets([]);
        setKubernetesServiceAccounts([]);
        setKubernetesTokens([]);
        setKubernetesSecrets([]);
    }, []);

    const fetchSyncStatus = useCallback(async () => {
        try {
            const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || myLocalStorage.getItem("latestTenant");
            if (!tenantName) return;
            const selection = getCloudAccountSelection(cloudProvider);
            if (!selection.configured) {
                setHasMultipleSnapshots(false);
                return;
            }
            const { data } = await axios.get(`${NGROK}/api/cloud/dashboard/sync-status`, {
                params: { tenantName, cloudProviderType: cloudProvider.toUpperCase() },
            });
            if (!Array.isArray(data) || data.length === 0) {
                setHasMultipleSnapshots(false);
                return;
            }
            const entry = selection.id
                ? data.find((s: any) => s.cloudAccountId === selection.id)
                : data[0];
            const prev = entry?.previousSnapshotVersionId;
            setHasMultipleSnapshots(Array.isArray(prev) && prev.length > 0);
        } catch (err) {
            console.error("Error fetching sync status:", err);
        }
    }, [cloudProvider]);

    const riskyLabel = hasMultipleSnapshots ? "Risk Increased" : "Risky";

    useEffect(() => {
        if (!hasCloudAccount) {
            clearNhiData();
            setLoading(false);
            setSecurityProgress(null);
            setSecurityProgressLoading(false);
            securityProgressLoadedRef.current = true;
            setHasMultipleSnapshots(false);
            return;
        }
        if (!cloudProviderChanges) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                if (cloudProvider === "gcp") {
                    setCloudServiceAccounts(await getGCPCloudServiceAccounts());
                    setCloudTokens(await getGCPCloudTokens());
                    setCloudSecrets(await getGCPCloudSecrets());
                    setKubernetesServiceAccounts(await getGCPKubernetesServiceAccounts());
                    setKubernetesTokens(await getGCPKubernetesTokens());
                    setKubernetesSecrets(await getGCPKubernetesSecrets());
                } else if (cloudProvider.toLowerCase() === "aws") {
                    setCloudServiceAccounts(await getAWSCloudIAMRole());
                    setCloudTokens(await getAWSCloudOIDC());
                    setCloudSecrets(await getAWSCloudSecrets());
                    setKubernetesServiceAccounts(await getAWSKubernetesServiceAccounts());
                    setKubernetesTokens(await getAWSKubernetesTokens());
                    setKubernetesSecrets(await getAWSKubernetesSecrets());
                } else if (cloudProvider === "azure") {
                    setCloudServiceAccounts(await getAzureCloudServiceAccounts());
                    setCloudTokens(await getAzureCloudTokens());
                    setCloudSecrets(await getAzureCloudSecrets());
                    setKubernetesServiceAccounts(await getAzureKubernetesServiceAccounts());
                    setKubernetesTokens(await getAzureKubernetesTokens());
                    setKubernetesSecrets(await getAzureKubernetesSecrets());
                } else {
                    // Clear all if unknown provider
                    setCloudServiceAccounts([]);
                    setCloudTokens([]);
                    setCloudSecrets([]);
                    setKubernetesServiceAccounts([]);
                    setKubernetesTokens([]);
                    setKubernetesSecrets([]);
                }
            } catch (err) {
                console.error("Error loading cloud data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cloudProvider, selectedTenant, cloudProviderChanges, hasCloudAccount, clearNhiData]);

    /** Map API riskChangeCategory to local status */
    const mapRiskCategory = (cat: string | null | undefined): "improved" | "worsened" | "neutral" => {
        switch (cat) {
            case "RISK_INCREASED": return "worsened";
            case "RISK_REDUCED": return "improved";
            case "NEUTRAL": return "neutral";
            default: return "neutral";
        }
    };

    const fetchSecurityProgress = useCallback(async () => {
        if (!securityProgressLoadedRef.current) setSecurityProgressLoading(true);
        try {
            const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || myLocalStorage.getItem("latestTenant");
            if (!tenantName) return;
            const selection = getCloudAccountSelection(cloudProvider);
            if (!selection.configured) {
                setSecurityProgress(null);
                setSecurityProgressLoading(false);
                securityProgressLoadedRef.current = true;
                return;
            }
            const cp = cloudProvider?.toLowerCase();
            let url = "";
            let params: any = { tenantName };
            if (cp === "gcp") {
                url = `${NGROK}/api/gcp/security-progress`;
                params.projectId = selection.id;
            } else if (cp === "aws") {
                url = `${NGROK}/api/aws/security-progress`;
                params.accountId = selection.id;
            } else if (cp === "azure") {
                url = `${NGROK}/api/azure/v1/security-progress`;
                params.subscriptionId = selection.id;
            }
            if (!url) return;
            const { data } = await axios.get(url, { params });
            // Store the nhis-specific sub-object for the NHI page
            setSecurityProgress(data?.overallSecurityProgress?.nhis || null);
        } catch (err) {
            console.error("Error loading security progress:", err);
            setSecurityProgress(null);
        } finally {
            setSecurityProgressLoading(false);
            securityProgressLoadedRef.current = true;
        }
    }, [cloudProvider]);

    const refetchNhiData = useCallback(async (options?: { live?: boolean }) => {
        if (!getCloudAccountSelection(cloudProvider).configured) {
            clearNhiData();
            setSecurityProgress(null);
            setSecurityProgressLoading(false);
            securityProgressLoadedRef.current = true;
            if (!options?.live) setLoading(false);
            return;
        }
        const isLiveRefresh = options?.live === true;
        if (!isLiveRefresh) {
            setLoading(true);
        }
        try {
            if (cloudProvider === "gcp") {
                setCloudServiceAccounts(await getGCPCloudServiceAccounts());
                setCloudTokens(await getGCPCloudTokens());
                setCloudSecrets(await getGCPCloudSecrets());
                setKubernetesServiceAccounts(await getGCPKubernetesServiceAccounts());
                setKubernetesTokens(await getGCPKubernetesTokens());
                setKubernetesSecrets(await getGCPKubernetesSecrets());
            } else if (cloudProvider.toLowerCase() === "aws") {
                setCloudServiceAccounts(await getAWSCloudIAMRole());
                setCloudTokens(await getAWSCloudOIDC());
                setCloudSecrets(await getAWSCloudSecrets());
                setKubernetesServiceAccounts(await getAWSKubernetesServiceAccounts());
                setKubernetesTokens(await getAWSKubernetesTokens());
                setKubernetesSecrets(await getAWSKubernetesSecrets());
            } else if (cloudProvider === "azure") {
                setCloudServiceAccounts(await getAzureCloudServiceAccounts());
                setCloudTokens(await getAzureCloudTokens());
                setCloudSecrets(await getAzureCloudSecrets());
                setKubernetesServiceAccounts(await getAzureKubernetesServiceAccounts());
                setKubernetesTokens(await getAzureKubernetesTokens());
                setKubernetesSecrets(await getAzureKubernetesSecrets());
            }
            await fetchSecurityProgress();
        } catch (err) {
            console.error("Error loading cloud data:", err);
        } finally {
            if (!isLiveRefresh) {
                setLoading(false);
            }
        }
    }, [cloudProvider, fetchSecurityProgress, clearNhiData]);

    useCloudSyncWatcher(cloudProvider, selectedTenant, refetchNhiData);

    const handleSync = async () => {
        if (!hasCloudAccount) return;
        try {
            if (cloudProvider === "aws") {
                await triggerCloudSync("aws", "/api/aws/ondemand", selectedTenant, refetchNhiData);
            } else if (cloudProvider === "azure") {
                await triggerCloudSync("azure", "/api/azure-sync/onDemand", selectedTenant, refetchNhiData);
            } else if (cloudProvider === "gcp") {
                await triggerCloudSync("gcp", "/api/gcp-sync/manual", selectedTenant, refetchNhiData);
            }
        } catch (err) {
            console.error("Error syncing cloud data:", err);
        }
    };

    useEffect(() => {
        if (!hasCloudAccount || !cloudProviderChanges) return;
        fetchSecurityProgress();
        fetchSyncStatus();
    }, [cloudProviderChanges, hasCloudAccount, fetchSecurityProgress, fetchSyncStatus]);

    const analyzedServiceAccounts = useMemo(() => {
        return cloudServiceAccounts.map(sa => ({
            ...sa,
            status: mapRiskCategory(sa.riskChangeCategory),
        }));
    }, [cloudServiceAccounts]);

    const improved = useMemo(() => analyzedServiceAccounts.filter(a => a.status === "improved"), [analyzedServiceAccounts]);
    const worsened = useMemo(() => analyzedServiceAccounts.filter(a => a.status === "worsened"), [analyzedServiceAccounts]);
    const neutral = useMemo(() => analyzedServiceAccounts.filter(a => a.status === "neutral"), [analyzedServiceAccounts]);

    // Reset the page to the complete entity list whenever the cloud/tenant context changes.
    useEffect(() => {
        setRiskFilter("all");
        setGovFilters({
            owner: null,
            activity: null,
            mostRisky: false,
            overPrivileged: false,
            adminManaged: false,
        });
        securityProgressLoadedRef.current = false;
    }, [cloudProvider, selectedTenant]);

    useEffect(() => {
        if (platformTab === "cloud" && activeTab === "secrets" && cloudProvider === "gcp") return;
        setGovFilters((current) => current.adminManaged
            ? { ...current, adminManaged: false }
            : current);
    }, [platformTab, activeTab, cloudProvider]);

    // Use security-progress API for the overall bar + hero cards
    const sp = securityProgress;
    const displayTotal = sp?.totalEntities ?? cloudServiceAccounts.length;
    const displayImproved = sp?.riskReducedCount ?? improved.length;
    const displayWorsened = sp?.riskIncreasedCount ?? worsened.length;
    const displayNeutral = sp?.neutralCount ?? neutral.length;

    const improvePct = sp?.greenBarPercentage ?? (displayTotal ? (displayImproved / displayTotal) * 100 : 0);
    const worsenPct = sp?.redBarPercentage ?? (displayTotal ? (displayWorsened / displayTotal) * 100 : 0);
    const neutralPct = displayTotal ? Math.max(100 - improvePct - worsenPct, 0) : 0;
    const dashboardLoading = hasCloudAccount && (loading || securityProgressLoading || !cloudProviderChanges || (syncStatus && cloudServiceAccounts.length === 0));

    const filteredCloudServiceAccounts = useMemo(() => {
        let list = analyzedServiceAccounts;
        if (riskFilter !== "all") {
            list = list.filter(a => a.status === riskFilter);
        }
        if (searchQuery) {
            const sq = searchQuery.toLowerCase();
            list = list.filter(
                (sa) =>
                    sa.email?.toLowerCase().includes(sq) ||
                    sa.algorithm?.toLowerCase().includes(sq) ||
                    sa.name?.toLowerCase().includes(sq) ||
                    sa.roleName?.toLowerCase().includes(sq)
            );
        }
        const highPrivOf = (sa: any) => sa.highPrivilegeRoleCount ?? sa.serviceDetails?.highPrivilegePolicyCount ?? sa.serviceDetails?.highPrivilegeRoleCount ?? 0;
        // Google-managed accounts always have an owner (Google Cloud), even before the audit sweep runs.
        const saHasOwner = (sa: any) =>
            (sa.owners || []).length > 0 || isGoogleManagedServiceAccount(cloudProvider, sa);
        if (govFilters.owner === "withOwner") {
            list = list.filter(saHasOwner);
        } else if (govFilters.owner === "withoutOwner") {
            list = list.filter((sa: any) => !saHasOwner(sa));
        }
        if (govFilters.activity === "dormant") {
            list = list.filter((sa: any) => sa.isDormant === true);
        }
        if (govFilters.mostRisky || govFilters.overPrivileged || govFilters.activity === "mostActive") {
            list = [...list].sort((a: any, b: any) => {
                const sorters = [
                    govFilters.mostRisky ? (b.alertCount || 0) - (a.alertCount || 0) : 0,
                    govFilters.overPrivileged ? highPrivOf(b) - highPrivOf(a) : 0,
                    govFilters.activity === "mostActive" ? (b.activity || 0) - (a.activity || 0) : 0,
                ];
                return sorters.find((delta) => delta !== 0) || 0;
            });
        }
        return list;
    }, [analyzedServiceAccounts, riskFilter, searchQuery, govFilters]);

    const filteredK8sServiceAccounts = kubernetesServiceAccounts.filter(
        (sa) =>
            sa.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            sa.namespace?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredCloudTokens = cloudTokens.filter(
        (token) =>
            token.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            token.type?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredCloudSecrets = useMemo(() => {
        const query = searchQuery.toLowerCase();
        const matchingSecrets = cloudSecrets.filter(
            (secret) =>
                secret.name?.toLowerCase().includes(query) ||
                secret.type?.toLowerCase().includes(query)
        );
        return applyGovernanceFilters(matchingSecrets, {
            ...govFilters,
            activity: govFilters.activity === "dormant" ? null : govFilters.activity,
            mostRisky: false,
            overPrivileged: false,
            adminManaged: cloudProvider === "gcp" ? govFilters.adminManaged : false,
        });
    }, [cloudSecrets, searchQuery, govFilters, cloudProvider]);

    const filteredK8sTokens = kubernetesTokens.filter(
        (token) =>
            token.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            token.type?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredK8sSecrets = useMemo(() => {
        const query = searchQuery.toLowerCase();
        const matchingSecrets = kubernetesSecrets.filter(
            (secret) =>
                secret.name?.toLowerCase().includes(query) ||
                secret.type?.toLowerCase().includes(query)
        );
        return applyGovernanceFilters(matchingSecrets, {
            ...govFilters,
            activity: govFilters.activity === "dormant" ? null : govFilters.activity,
            mostRisky: false,
            overPrivileged: false,
            adminManaged: false,
        });
    }, [kubernetesSecrets, searchQuery, govFilters]);

    const handleTenantChange = (tenant: string) => {
        setSelectedTenant(tenant);
    };

    const handleCloudProvider = (value: any) => {
        setCloudProviderChanges(value);
    }

    const getK8sNHILink = (item: any, activeTab: any) => {
        const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
        const cloudType = cloudProvider;

        let type = "K8SERVICE"
        if (activeTab === "tokens") type = "K8TOKEN"
        else if (activeTab === "secrets") type = "K8SECRET";
        else type = "K8SERVICE"

        // Determine correct URL based on cloud type (Azure/GCP currently supported)
        const url =
            cloudType === "AZURE"
                ? "/azure-directory/nhi/service-account-details"
                : "/gcp/nhi/service-account-details";

        // Build state object dynamically depending on item type
        const stateObj: any = {
            tenantName: latestTenant,
            cloudType,
            cloudNHIType: type,
            secretName: item?.name,
            nameSpace: item?.namespace || item?.nameSpace,
            from: "NonHumanIdentitiesTable",
        };

        // Attach the specific item details
        if (type === "K8SECRET") {
            stateObj.secretDetails = item;
        } else if (type === "K8SERVICE") {
            stateObj.serviceDetails = item;
        } else if (type === "K8TOKEN") {
            stateObj.tokenDetails = item;
        }

        // Save to local storage (for persistence)
        myLocalStorage.setItem("nhi-details", JSON.stringify(stateObj));

        // Return object (if used for navigation in other parts)
        return {
            pathname: url,
            state: stateObj,
        };
    };

    const getNHILink = (item: any, type: string, cloudProvider: string) => {
        const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
        const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
        const latestprojectId = myLocalStorage.getItem("gcpSelectedProjectId");

        const cloudType = cloudProvider?.toUpperCase();

        const pathname =
            cloudType === "AWS"
                ? "/aws/nhi/service-account-details"
                : cloudType === "AZURE"
                    ? "/azure-directory/nhi/service-account-details"
                    : "/gcp/nhi/service-account-details";

        const stateObj: any = {
            tenantName: latestTenant,
            cloudType,
            from: "NonHumanIdentitiesTable",
        };

        switch (type.toUpperCase()) {


            case "SERVICE_ACCOUNT":
                stateObj.item = item;
                stateObj.cloudNHIType = "SERVICE_ACCOUNT";
                stateObj.serviceAccountEmail = item.email;
                stateObj.projectId = latestprojectId || item.projectId || null;
                stateObj.accountId = selectedAwsAccount || item.accountId || null;
                stateObj.syncedAt =
                    item.serviceDetails?.syncedAt ||
                    item.syncedAt ||
                    item.serviceDetails?.createDate ||
                    item.createDate ||
                    item.validityPeriod ||
                    "";
                stateObj.roles =
                    item.roles ||
                    item.serviceroles ||
                    item.serviceDetails?.roles ||
                    item.serviceDetails?.serviceroles ||
                    [];
                stateObj.roleDetails =
                    item.iamRoleDetails ||
                    item.roleDetails ||
                    item.serviceDetails ||
                    null;
                stateObj.principalId = item.principalId || item.serviceDetails?.principalId || "";
                stateObj.sa = item;
                stateObj.serviceDetails = item.serviceDetails || null;
                stateObj.createdAt =
                    item.serviceDetails?.createDate ||
                    item.createDate ||
                    item.serviceDetails?.syncedAt ||
                    item.syncedAt ||
                    item.validityPeriod ||
                    "";
                myLocalStorage.setItem("nhi-details", JSON.stringify(stateObj));

                break;

            case "SECRET":
                myLocalStorage.setItem("nhi-details", JSON.stringify(item.secretDetails));
                stateObj.cloudNHIType = "SECRET";
                stateObj.secretName =
                    cloudType === "AWS" ? item?.secretArn : item?.name;
                stateObj.cloudSecret = item;
                stateObj.createdAt = item.createdAt
                break;


            case "TOKEN":
                myLocalStorage.setItem("nhi-details", JSON.stringify(item.tokenDetails));
                stateObj.cloudNHIType = "TOKEN";
                stateObj.secretName = item?.secretName || item?.name;
                stateObj.cloudTokenDetails = item;
                stateObj.createdAt = item.createdAt
                break;


            case "OIDC":
                myLocalStorage.setItem("nhi-details", JSON.stringify(item.oidcDetails));
                stateObj.cloudNHIType = "OIDC";
                stateObj.oidcARN = item?.name;
                stateObj.providerArn = item?.name;
                stateObj.oidc = item;
                stateObj.createDate = item.createdAt
                break;

            default:
                console.warn(`Unknown NHI type passed: ${type}`);
                break;
        }


        return {
            pathname,
            state: stateObj,
        };
    };


    if (!hasCloudAccount) {
        return (
            <div className="h-full flex flex-col bg-background">
                <div className="p-6">
                    <CloudFilters
                        cloudProvider={cloudProvider}
                        onTenantChange={handleTenantChange}
                        onCloudAccountChange={handleCloudProvider}
                        isAdmin={true}
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        searchPlaceholder="Search entities…"
                        onSync={handleSync}
                        syncDisabled
                        syncLoading={false}
                    />
                    <EmptyStateCard
                        icon={Cloud}
                        title={accountSelection.title}
                        description={accountSelection.description}
                    />
                </div>
            </div>
        );
    }


    return (
        <div className="h-full flex flex-col bg-background">
            <div className="p-6">
                {/* Cloud Filters */}
                <CloudFilters
                    cloudProvider={cloudProvider}
                    onTenantChange={handleTenantChange}
                    onCloudAccountChange={handleCloudProvider}
                    isAdmin={true}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    searchPlaceholder="Search entities…"
                    onSync={handleSync}
                    syncDisabled={syncStatus}
                    syncLoading={syncStatus}
                />

                {/* Dashboard Header Content (Progress, Trends, Quick Info) */}
                <div className="mb-6 space-y-6">
                    {dashboardLoading ? (
                        <SecurityProgressSkeleton riskyLabel={riskyLabel} />
                    ) : (
                        <div className="cs-progress-hero" style={{ margin: 0 }}>
                            <div className="cs-progress-details">
                                <div className="cs-progress-title-row">
                                    <h3 className="cs-progress-main-title">Overall Security Progress</h3>
                                    <span className="cs-progress-subtitle">{displayTotal} entities analyzed</span>
                                </div>

                                <div className="cs-bar-wrap">
                                    <div className="cs-bar-track">
                                        <div className="cs-bar-fill improved" style={{ width: `${improvePct}%` }} />
                                        <div className="cs-bar-fill worsened" style={{ width: `${worsenPct}%` }} />
                                        <div className="cs-bar-fill unchanged" style={{ width: `${neutralPct}%` }} />
                                    </div>
                                </div>

                                <div className="cs-bar-legend">
                                    <div className="cs-bar-legend-item">
                                        <div className="cs-bar-legend-dot improved" />
                                        <TrendingDown className="cs-bar-legend-icon improved" />
                                        <span className="cs-bar-legend-count">{displayImproved}</span>
                                        <span className="cs-bar-legend-label">Risk Reduced</span>
                                    </div>
                                    <div className="cs-bar-legend-item">
                                        <div className="cs-bar-legend-dot worsened" />
                                        <TrendingUp className="cs-bar-legend-icon worsened" />
                                        <span className="cs-bar-legend-count">{displayWorsened}</span>
                                        <span className="cs-bar-legend-label">{riskyLabel}</span>
                                    </div>
                                    <div className="cs-bar-legend-item">
                                        <div className="cs-bar-legend-dot unchanged" />
                                        <ShieldCheck className="cs-bar-legend-icon unchanged" />
                                        <span className="cs-bar-legend-count">{displayNeutral}</span>
                                        <span className="cs-bar-legend-label">Neutral</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === "service-accounts" && (
                        <AlertTrendPanel cloudProvider={cloudProvider} scope="SERVICE_ACCOUNT" />
                    )}
                </div>

                {/* Navigation Control Center */}
                <Card className="cloud-card-surface mb-6 bg-card border-border/50 overflow-hidden rounded-2xl">
                    <div className="flex flex-col md:flex-row md:items-center justify-between p-3 gap-6">

                        {/* Left: Entity Tabs */}
                        <div className="flex items-center">
                            <div className="cs-tabs !m-0 !w-auto !bg-transparent">
                                <button
                                    onClick={() => setActiveTab("service-accounts")}
                                    className={`cs-tab ${activeTab === "service-accounts" ? "active" : ""}`}
                                    data-testid="tab-service-accounts"
                                >
                                    <Shield className="w-4 h-4" />
                                    {platformTab === "cloud" && cloudProvider.toLowerCase() === "aws" ? "IAM Role" : "Service Accounts"}
                                </button>
                                <button
                                    onClick={() => setActiveTab("secrets")}
                                    className={`cs-tab ${activeTab === "secrets" ? "active" : ""}`}
                                    data-testid="tab-secrets"
                                >
                                    <Lock className="w-4 h-4" />
                                    Secrets
                                </button>
                                <button
                                    onClick={() => setActiveTab("tokens")}
                                    className={`cs-tab ${activeTab === "tokens" ? "active" : ""}`}
                                    data-testid="tab-tokens"
                                >
                                    <Key className="w-4 h-4" />
                                    {platformTab === "cloud" && cloudProvider.toLowerCase() === "aws" ? "OIDC" : "Tokens"}
                                </button>
                            </div>
                        </div>

                        {/* Right: Platform Tabs */}
                        <div className="flex items-center">
                            <div className="cs-tabs !m-0 !w-auto !bg-transparent">
                                <button
                                    onClick={() => setPlatformTab("cloud")}
                                    className={`cs-tab ${platformTab === "cloud" ? "active" : ""}`}
                                    data-testid="tab-cloud-nhi"
                                >
                                    {cloudProvider === 'gcp' ? (
                                        <FaGoogle className="w-4 h-4" />
                                    ) : cloudProvider === 'aws' ? (
                                        <SiAmazon className="w-4 h-4" />
                                    ) : (
                                        <VscAzure className="w-4 h-4" />
                                    )}
                                    Cloud NHI
                                </button>
                                <button
                                    onClick={() => setPlatformTab("kubernetes")}
                                    className={`cs-tab ${platformTab === "kubernetes" ? "active" : ""}`}
                                    data-testid="tab-kubernetes-nhi"
                                >
                                    <SiKubernetes className="w-4 h-4" />
                                    Kubernetes NHI
                                </button>
                            </div>
                        </div>

                    </div>
                </Card>

                {/* Cloud NHI Content */}
                {platformTab === "cloud" && (
                    <div className="space-y-4">

                        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">

                            <TabsContent value="service-accounts" className="space-y-2">
                                <div className="mb-6 space-y-6">
                                    {dashboardLoading ? (
                                        <SummaryCardsSkeleton
                                            cards={[
                                                { label: "Total Services", icon: <Shield className="w-6 h-6" />, colorClass: "blue" },
                                                { label: riskyLabel, icon: <TrendingUp className="w-6 h-6" />, colorClass: "red" },
                                                { label: "Risk Reduced", icon: <TrendingDown className="w-6 h-6" />, colorClass: "green" },
                                                { label: "Neutral", icon: <ShieldCheck className="w-6 h-6" />, colorClass: "gray" },
                                            ]}
                                        />
                                    ) : (
                                        <div className="cs-hero" style={{ margin: 0 }}>
                                            <div className="cs-hero-card">
                                                <div className="cs-hero-icon blue"><Shield className="w-6 h-6" /></div>
                                                <div className="cs-hero-info">
                                                    <span className="cs-hero-value">{displayTotal}</span>
                                                    <span className="cs-hero-label">Total Services</span>
                                                </div>
                                            </div>
                                            <div className="cs-hero-card">
                                                <div className="cs-hero-icon red"><TrendingUp className="w-6 h-6" /></div>
                                                <div className="cs-hero-info">
                                                    <span className="cs-hero-value">{displayWorsened}</span>
                                                    <span className="cs-hero-label">{riskyLabel}</span>
                                                </div>
                                            </div>
                                            <div className="cs-hero-card">
                                                <div className="cs-hero-icon green"><TrendingDown className="w-6 h-6" /></div>
                                                <div className="cs-hero-info">
                                                    <span className="cs-hero-value">{displayImproved}</span>
                                                    <span className="cs-hero-label">Risk Reduced</span>
                                                </div>
                                            </div>
                                            <div className="cs-hero-card">
                                                <div className="cs-hero-icon gray"><ShieldCheck className="w-6 h-6" /></div>
                                                <div className="cs-hero-info">
                                                    <span className="cs-hero-value">{displayNeutral}</span>
                                                    <span className="cs-hero-label">Neutral</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Status Filters */}
                                    <div className="cs-tabs">
                                        <button className={`cs-tab all-tab ${riskFilter === "all" ? "active" : ""}`} onClick={() => setRiskFilter("all")}>
                                            <ShieldCheck className="w-4 h-4" /> All <span className="cs-tab-count">{dashboardLoading ? "..." : analyzedServiceAccounts.length}</span>
                                        </button>
                                        <button className={`cs-tab worsened-tab ${riskFilter === "worsened" ? "active" : ""}`} onClick={() => setRiskFilter("worsened")}>
                                            <TrendingUp className="w-4 h-4" /> {riskyLabel} <span className="cs-tab-count">{dashboardLoading ? "..." : worsened.length}</span>
                                        </button>
                                        <button className={`cs-tab improved-tab ${riskFilter === "improved" ? "active" : ""}`} onClick={() => setRiskFilter("improved")}>
                                            <TrendingDown className="w-4 h-4" /> Risk Reduced <span className="cs-tab-count">{dashboardLoading ? "..." : improved.length}</span>
                                        </button>
                                        <button className={`cs-tab unchanged-tab ${riskFilter === "neutral" ? "active" : ""}`} onClick={() => setRiskFilter("neutral")}>
                                            <ShieldCheck className="w-4 h-4" /> Neutral <span className="cs-tab-count">{dashboardLoading ? "..." : neutral.length}</span>
                                        </button>
                                    </div>
                                </div>

                                <GovernanceFilterPanel
                                    entityLabel="service accounts"
                                    hasFilter={hasGovernanceFilter}
                                    isActive={isGovernanceFilterActive}
                                    onToggle={toggleGovernanceFilter}
                                    onClear={clearGovernanceFilters}
                                />

                                {(loading || !cloudProviderChanges) && filteredCloudServiceAccounts.length === 0 ? (
                                    <div className="space-y-2">
                                        {[1, 2, 3, 4].map((i) => (
                                            <Card key={i} className="cloud-card-surface animate-pulse rounded-2xl">
                                                <CardContent className="p-4 flex items-center gap-4">
                                                    <div className="w-8 h-8 rounded-full bg-muted/60 flex-shrink-0" />
                                                    <div className="flex-1 space-y-2">
                                                        <div className="h-4 bg-muted/60 rounded w-1/4" />
                                                        <div className="h-3 bg-muted/60 rounded w-1/2" />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                ) : filteredCloudServiceAccounts.length === 0 ? (
                                    <Card className="cloud-card-surface border-dashed rounded-2xl">
                                        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                                            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                                <CloudIcon provider={cloudProvider} />
                                            </div>
                                            <h3 className="text-lg font-semibold mb-2">No {cloudProvider.toUpperCase()} services found</h3>
                                            <p className="text-sm text-muted-foreground max-w-md mb-6">
                                                You currently don't have any services account.
                                            </p>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    filteredCloudServiceAccounts.map((sa) => {
                                        const userLink = getNHILink(sa, "SERVICE_ACCOUNT", cloudProvider);
                                        const isExpanded = expandedRoles[sa.id || sa.email];
                                        const visibleRoles = isExpanded ? sa.serviceroles || [] : (sa.serviceroles || []).slice(0, 3);
                                        const hasMore = (sa.serviceroles || []).length > 3;

                                        return (
                                            <Card
                                                key={sa.id}
                                                className="cloud-list-card cursor-pointer rounded-2xl"
                                                data-testid={`card-sa-${sa.id}`}
                                                onClick={() => navigate(userLink.pathname, { state: userLink.state })}
                                            >
                                                <CardContent className="p-4">

                                                    <div className="flex items-start gap-3 hover:no-underline">
                                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                            <Shield className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                                                                <h3 className="font-semibold text-[13px] truncate block hover:no-underline">{sa.email}</h3>
                                                                <Badge
                                                                    variant={sa.isDormant ? "secondary" : "default"}
                                                                    className={getDormancyBadgeClass(Boolean(sa.isDormant))}
                                                                >
                                                                    {sa.isDormant ? "Dormant" : "Active"}
                                                                </Badge>
                                                                {sa.managedType && (
                                                                    <Badge
                                                                        variant={isUserManagedValue(sa.managedType) ? "default" : "outline"}
                                                                        className={getManagedBadgeClass(sa.managedType)}
                                                                    >
                                                                        {sa.managedType}
                                                                    </Badge>
                                                                )}
                                                                <TotalAlertsBadge count={sa.alertCount} />
                                                                {sa.riskTags && sa.riskTags.length > 0 && filterRiskTags(sa.riskTags)
                                                                    .map((tag: string, idx: number) => (
                                                                        <RiskTagBadge key={`${tag}-${idx}`} tag={tag} tagInfo={sa.tagInfo} />
                                                                    ))}
                                                            </div>
                                                            <div className="mb-1.5" onClick={(e) => e.stopPropagation()}>
                                                                {isGoogleManagedServiceAccount(cloudProvider, sa) ? (
                                                                    <div className="flex items-center gap-1.5">
                                                                        <div className="text-[11px] font-medium text-muted-foreground mr-1">Owner:</div>
                                                                        <Badge
                                                                            variant="secondary"
                                                                            className="text-[11px] h-5 bg-primary/10 text-primary border-primary/20"
                                                                        >
                                                                            {GOOGLE_CLOUD_OWNER}
                                                                        </Badge>
                                                                    </div>
                                                                ) : (
                                                                    <NhiOwnerSelector
                                                                        cloudProvider={cloudProvider}
                                                                        tenantName={latestTenantName}
                                                                        identifier={getNhiIdentifier(sa, cloudProvider, latestTenantName)}
                                                                        owners={sa.owners || []}
                                                                        onUpdated={() => refetchNhiData()}
                                                                    />
                                                                )}
                                                            </div>
                                                            {cloudProvider !== "aws" && cloudProvider !== "gcp" && sa.keyId && (
                                                                <div className="text-[11px] text-muted-foreground mt-1.5">
                                                                    <span className="font-medium">Key ID:</span> {sa.keyId}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                            <div className="text-[11px] text-muted-foreground">Activity</div>
                                                            <ActivityBar count={sa.activity} className="w-20" />
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 pt-3 border-t flex items-start gap-6">
                                                        <div className="flex-1 min-w-0 space-y-2">
                                                            {sa.serviceroles && sa.serviceroles.length > 0 && (
                                                                <div className="flex flex-wrap items-center gap-1.5">
                                                                    <div className="text-[11px] font-medium text-muted-foreground mr-1">Roles:</div>
                                                                    {visibleRoles.map((role: any, idx: any) => (
                                                                        <Badge key={idx} variant={idx === 0 ? "default" : "secondary"} className="text-xs h-5">
                                                                            {role}
                                                                        </Badge>
                                                                    ))}
                                                                    {hasMore && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                toggleRoles(sa.id || sa.email);
                                                                            }}
                                                                            className="text-xs text-primary hover:underline font-medium flex items-center gap-0.5"
                                                                        >
                                                                            {isExpanded ? (
                                                                                <>
                                                                                    <ChevronUp className="w-3 h-3" />
                                                                                    Less
                                                                                </>
                                                                            ) : (
                                                                                <>+{(sa.serviceroles || []).length - 3}</>
                                                                            )}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {sa.algorithm && (
                                                                <div className="flex flex-wrap items-center gap-1.5">
                                                                    <div className="text-[11px] font-medium text-muted-foreground mr-1">Key Algo:</div>
                                                                    <Badge variant="secondary" className="text-xs h-5">{sa.algorithm}</Badge>
                                                                </div>
                                                            )}
                                                            <NhiFrequentUsers frequentUsers={sa.frequentUsers || []} />
                                                        </div>
                                                        <div className="flex-shrink-0 text-right">
                                                            <div className="text-xs text-muted-foreground mb-1.5">
                                                                Last Activity
                                                            </div>
                                                            <div className="text-xs font-medium">
                                                                {getDisplayValue(sa.lastActivityMessage, "-")}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mt-2 flex items-center justify-between">
                                                        <div className="flex items-center gap-1.5">
                                                            <div className="text-[11px] font-medium text-muted-foreground mr-1">Synced :</div>
                                                            <Badge variant="secondary" className="text-[10px] h-5 bg-muted/50">{getDisplayValue(sa.validityPeriod)}</Badge>
                                                        </div>
                                                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                            <div>
                                                                <DetailsDialog data={sa} title={`${cloudProvider} User: ${sa.email}`} />
                                                            </div>
                                                            <NhiDeleteButton
                                                                cloudProvider={cloudProvider}
                                                                identifier={getNhiIdentifier(sa, cloudProvider, latestTenantName)}
                                                                onDeleted={() => refetchNhiData()}
                                                            />
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    })
                                )}
                            </TabsContent>

                            <TabsContent value="tokens" className="space-y-2">
                                {filteredCloudTokens.length === 0 ? (
                                    <Card className="cloud-card-surface border-dashed rounded-2xl">
                                        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                                            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                                <CloudIcon provider={cloudProvider} />
                                            </div>
                                            <h3 className="text-lg font-semibold mb-2">No {cloudProvider.toUpperCase()} entities found</h3>
                                            <p className="text-sm text-muted-foreground max-w-md mb-6">
                                                You currently don't have any entities for this account.
                                            </p>
                                        </CardContent>
                                    </Card>

                                ) : (
                                    filteredCloudTokens.map((token) => {
                                        let type = cloudProvider.toLowerCase() === "aws" ? "OIDC" : "TOKEN";
                                        let tokenLink = getNHILink(token, type, cloudProvider)

                                        return (
                                            <Card
                                                key={token.id}
                                                className="cloud-list-card cursor-pointer rounded-2xl"
                                                data-testid={`card-token-${token.id}`}
                                                onClick={() => navigate(tokenLink.pathname, { state: tokenLink.state })}
                                            >
                                                <CardContent className="p-4">
                                                    <div className="flex items-start gap-3 hover:no-underline">
                                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                            <Key className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                                <h3 className="font-semibold text-sm truncate block hover:no-underline">{token.name}</h3>
                                                                <TotalAlertsBadge count={token.alertCount} />
                                                                {token.riskTags && token.riskTags.length > 0 && filterRiskTags(token.riskTags)
                                                                    .map((tag: string, idx: number) => (
                                                                        <RiskTagBadge key={`${tag}-${idx}`} tag={tag} tagInfo={token.tagInfo} />
                                                                    ))}
                                                            </div>
                                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                                <Badge variant="secondary" className="text-xs h-5">{token.type}</Badge>
                                                            </div>
                                                            <p className="text-xs font-mono text-muted-foreground mb-2">{token.keyId}</p>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                            <div className="text-xs text-muted-foreground">Activity</div>
                                                            <ActivityBar count={token.activity} className="w-20" />
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 pt-3 border-t flex items-start gap-6">
                                                        <div className="flex-1 min-w-0 flex items-center gap-6 flex-wrap">
                                                            <div className="flex items-center gap-1.5">
                                                                <div className="text-[11px] font-medium text-muted-foreground mr-1">Created :</div>
                                                                <Badge variant="secondary" className="text-[10px] h-5 bg-muted/50">{token.createdAt}</Badge>
                                                            </div>
                                                            <div className="flex items-center gap-1.5">
                                                                <div className="text-[11px] font-medium text-muted-foreground mr-1">Expires :</div>
                                                                <Badge variant="secondary" className="text-[10px] h-5 bg-muted/50">{token.expiresAt}</Badge>
                                                            </div>
                                                        </div>
                                                        <div className="flex-shrink-0">
                                                            <div onClick={(e) => e.stopPropagation()}>
                                                                <DetailsDialog data={token} title={`${cloudProvider} Role: ${token.name}`} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    })
                                )}
                            </TabsContent>

                            <TabsContent value="secrets" className="space-y-2">
                                <GovernanceFilterPanel
                                    entityLabel="secrets"
                                    hasFilter={hasSecretGovernanceFilter}
                                    isActive={isGovernanceFilterActive}
                                    onToggle={toggleGovernanceFilter}
                                    onClear={clearGovernanceFilters}
                                    showAdminManaged={cloudProvider === "gcp"}
                                    hideSecretUnsupportedFilters
                                    action={cloudProvider === "gcp" ? (
                                        <div className="flex flex-wrap items-center gap-2">
                                            <StartSecretScanButton controller={secretScanController} />
                                            <Button
                                                type="button"
                                                size="sm"
                                                className="h-10 gap-2 rounded-lg px-5 text-sm font-semibold"
                                                onClick={() => setIsAddAdminSecretOpen(true)}
                                                data-testid="button-add-admin-secret"
                                            >
                                                <Plus className="h-4 w-4" />
                                                Add Secret
                                            </Button>
                                        </div>
                                    ) : undefined}
                                />
                                <SecretDiscoveryScanPanel controller={secretScanController} />
                                {filteredCloudSecrets.length === 0 ? (
                                    <Card className="cloud-card-surface border-dashed rounded-2xl">
                                        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                                            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                                <CloudIcon provider={cloudProvider} />
                                            </div>
                                            <h3 className="text-lg font-semibold mb-2">No {cloudProvider.toUpperCase()} entities found</h3>
                                            <p className="text-sm text-muted-foreground max-w-md mb-6">
                                                You currently don't have any entities for this account.
                                            </p>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    filteredCloudSecrets.map((secret) => {
                                        const secretLink = getNHILink(secret, "SECRET", cloudProvider);
                                        const discoveryLocationCount = Number(
                                            secret.discoveryLocationCount ??
                                            getSecretDiscoveryLocationCount(
                                                secretFootprintCounts,
                                                secret.fullName || secret.name,
                                            )
                                        );

                                        return (
                                            <Card
                                                key={secret.id}
                                                className="cloud-list-card cursor-pointer rounded-2xl"
                                                data-testid={`card-secret-${secret.id}`}
                                                onClick={() => navigate(secretLink.pathname, { state: secretLink.state })}
                                            >
                                                <CardContent className="p-4">
                                                    <div className="flex items-start gap-3 hover:no-underline">
                                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                            <Lock className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                                <h3 className="font-semibold text-sm truncate block hover:no-underline">{secret.name}</h3>
                                                                <TotalAlertsBadge count={secret.alertCount} />
                                                                {secret.riskTags && secret.riskTags.length > 0 && filterRiskTags(secret.riskTags)
                                                                    .map((tag: string, idx: number) => (
                                                                        <RiskTagBadge key={`${tag}-${idx}`} tag={tag} tagInfo={secret.tagInfo} />
                                                                    ))}
                                                            </div>
                                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                                <Badge variant="secondary" className="text-xs h-5">{secret.type || "Secret"}</Badge>
                                                                <Badge
                                                                    variant={isUserManagedValue(secret.isManagedSecret || "User Managed") || isAdminManagedValue(secret.isManagedSecret) ? "default" : "outline"}
                                                                    className={getManagedBadgeClass(secret.isManagedSecret || "User Managed")}
                                                                >
                                                                    {secret.isManagedSecret || "User Managed"}
                                                                </Badge>
                                                                {discoveryLocationCount > 0 && (
                                                                    <Badge
                                                                        variant="outline"
                                                                        className="h-5 gap-1 border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300"
                                                                    >
                                                                        <ScanSearch className="h-3 w-3" />
                                                                        {discoveryLocationCount} locations found
                                                                    </Badge>
                                                                )}
                                                                {secret.riskTags && secret.riskTags.length > 0 && filterRiskTags(secret.riskTags)
                                                                    .map((tag: string, idx: number) => (
                                                                        <RiskTagBadge key={`${tag}-${idx}`} tag={tag} tagInfo={secret.tagInfo} />
                                                                    ))}
                                                            </div>
                                                            <div className="text-[11px] text-muted-foreground mb-2 break-all leading-relaxed">
                                                                <span className="font-medium text-foreground/80">Path:</span> {getDisplayValue(secret.fullName || secret.name)}
                                                            </div>
                                                            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                                                {"keyNames" in secret && Array.isArray(secret.keyNames) && secret.keyNames.length > 0 && (
                                                                    secret.keyNames.slice(0, 3).map((keyName: string) => (
                                                                        <Badge key={keyName} variant="outline" className="text-[10px] h-5">
                                                                            {keyName}
                                                                        </Badge>
                                                                    ))
                                                                )}
                                                                {Array.isArray(secret.keyNames) && secret.keyNames.length > 3 && (
                                                                    <Badge variant="outline" className="text-[10px] h-5">
                                                                        +{secret.keyNames.length - 3} more
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                            <div className="text-xs text-muted-foreground">Activity</div>
                                                            <ActivityBar count={secret.activity} className="w-20" />
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 pt-3 border-t flex items-start gap-6">
                                                        <div className="flex-1 min-w-0">
                                                            {renderSecretCardMeta(secret)}
                                                        </div>
                                                        <div className="flex flex-shrink-0 self-stretch flex-col items-end justify-between gap-3">
                                                            <div className="text-right">
                                                                <div className="text-xs text-muted-foreground mb-1.5">Last Activity</div>
                                                                <div className="text-xs font-medium">
                                                                    {getDisplayValue(secret.lastActivity, "No activity in last 30 days")}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                                <DetailsDialog data={secret} title={`${cloudProvider} Secret: ${secret.name}`} />
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="gap-2 border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    data-testid={`button-delete-secret-${secret.id}`}
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                    Delete
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    })
                                )}
                            </TabsContent>
                        </Tabs>
                    </div>
                )}

                {cloudProvider === "gcp" && (
                    <AddAdminSecretDialog
                        open={isAddAdminSecretOpen}
                        onOpenChange={setIsAddAdminSecretOpen}
                        tenantName={selectedTenant || latestTenantName}
                        projectId={accountSelection.id}
                        onCreated={() => refetchNhiData()}
                    />
                )}

                {/* Kubernetes NHI Content */}
                {platformTab === "kubernetes" && (
                    <div className="space-y-4">

                        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">

                            <TabsContent value="service-accounts" className="space-y-2">
                                {filteredK8sServiceAccounts.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-muted-foreground">No service accounts found</p>
                                    </div>
                                ) : (
                                    filteredK8sServiceAccounts.map((sa) => {
                                        let nhiLink = getK8sNHILink(sa, activeTab);
                                        return (
                                            <Card 
                                                key={sa.id} 
                                                className="cloud-list-card cursor-pointer rounded-2xl" 
                                                data-testid={`card-k8s-sa-${sa.id}`}
                                                onClick={() => navigate(nhiLink.pathname, { state: nhiLink.state })}
                                            >
                                                <CardContent className="p-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                            <Shield className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">

                                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                                <h3 className="font-semibold text-sm truncate block hover:no-underline">{sa.name}</h3>
                                                                <Badge
                                                                    variant={sa.isDormant ? "secondary" : "default"}
                                                                    className={getDormancyBadgeClass(Boolean(sa.isDormant))}
                                                                >
                                                                    {sa.isDormant ? "Dormant" : "Active"}
                                                                </Badge>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2">

                                                                <div>
                                                                    <span className="text-muted-foreground">Namespace:</span> <span className="font-medium">{sa.namespace}</span>
                                                                </div>

                                                                <div>
                                                                    <span className="text-muted-foreground">Cluster:</span> <span className="font-medium">{sa.clusterId}</span>
                                                                </div>
                                                                <div className="col-span-2">
                                                                    <span className="text-muted-foreground">Synced:</span> {sa.syncedAt}
                                                                </div>
                                                            </div>
                                                            {sa.tags && sa.tags.length > 0 && (
                                                                <div className="flex flex-wrap gap-1">
                                                                    {sa.tags.map((tag: any, idx: any) => (
                                                                        <Badge key={idx} variant="outline" className="text-xs h-5 px-2">
                                                                            {tag}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                            <div className="text-xs text-muted-foreground">Activity</div>
                                                            <ActivityBar count={sa.activity} className="w-20" />
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 pt-3 border-t flex items-center justify-between gap-4">
                                                        <div className="text-left">
                                                            <div className="text-xs text-muted-foreground mb-1.5">
                                                                Last Activity
                                                            </div>
                                                            <div className="text-xs font-medium">
                                                                {getDisplayValue(sa.lastActivityMessage, "-")}
                                                            </div>
                                                        </div>
                                                        <div onClick={(e) => e.stopPropagation()}>
                                                            <DetailsDialog data={sa} title={`K8s Service Account: ${sa.name}`} />
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    })
                                )}
                            </TabsContent>

                            <TabsContent value="tokens" className="space-y-2">
                                {filteredK8sTokens.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-muted-foreground">No tokens found</p>
                                    </div>
                                ) : (
                                    filteredK8sTokens.map((token) => {
                                        let nhiLink = getK8sNHILink(token, activeTab);

                                        return (

                                            <Card 
                                                key={token.id} 
                                                className="cloud-list-card cursor-pointer rounded-2xl" 
                                                data-testid={`card-k8s-token-${token.id}`}
                                                onClick={() => navigate(nhiLink.pathname, { state: nhiLink.state })}
                                            >
                                                <CardContent className="p-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                            <Key className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-2">

                                                                <h3 className="font-semibold text-sm truncate block hover:no-underline">{token.name}</h3>
                                                                <Badge variant="secondary" className="text-xs h-5">{token.type}</Badge>

                                                            </div>
                                                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                                                <div>
                                                                    <span className="text-muted-foreground">Namespace:</span> <span className="font-medium">{token.namespace}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-muted-foreground">Cluster:</span> <span className="font-medium">{token.clusterId}</span>
                                                                </div>
                                                                <div className="col-span-2">
                                                                    <span className="text-muted-foreground">Synced:</span> {token.syncedAt}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                            <div className="text-xs text-muted-foreground">Activity</div>
                                                            <ActivityBar count={token.activity} className="w-20" />
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 pt-3 border-t flex justify-end">
                                                        <div onClick={(e) => e.stopPropagation()}>
                                                            <DetailsDialog data={token} title={`K8s Token: ${token.name}`} />
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    })
                                )}
                            </TabsContent>

                            <TabsContent value="secrets" className="space-y-2">
                                <GovernanceFilterPanel
                                    entityLabel="secrets"
                                    hasFilter={hasSecretGovernanceFilter}
                                    isActive={isGovernanceFilterActive}
                                    onToggle={toggleGovernanceFilter}
                                    onClear={clearGovernanceFilters}
                                    hideSecretUnsupportedFilters
                                />
                                {filteredK8sSecrets.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-muted-foreground">No secrets found</p>
                                    </div>
                                ) : (
                                    filteredK8sSecrets.map((secret) => {
                                        let nhiLink = getK8sNHILink(secret, activeTab);

                                        return (
                                            <Card
                                                key={secret.id}
                                                className="cloud-list-card cursor-pointer rounded-2xl"
                                                data-testid={`card-k8s-secret-${secret.id}`}
                                                onClick={() => navigate(nhiLink.pathname, { state: nhiLink.state })}
                                            >
                                                <CardContent className="p-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                            <Lock className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                                <h3 className="font-semibold text-sm tracking-tight block hover:no-underline">{secret.name}</h3>
                                                                <Badge variant="secondary" className="text-[10px] h-5">{secret.type}</Badge>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] mb-2 leading-relaxed">
                                                                <div>
                                                                    <span className="text-muted-foreground">Namespace:</span> <span className="font-medium">{secret.namespace}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-muted-foreground">Cluster:</span> <span className="font-medium">{secret.clusterId}</span>
                                                                </div>
                                                                <div className="col-span-2">
                                                                    <span className="text-muted-foreground">Synced:</span> {secret.syncedAt}
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-wrap gap-1.5 text-[11px]">
                                                                <span className="inline-flex items-center rounded-md bg-muted/70 px-2 py-1 text-muted-foreground">
                                                                    <span className="mr-1 font-medium text-foreground">Owner:</span> {getDisplayValue(secret.cloudResourceAccountId || secret.wsTenantName)}
                                                                </span>
                                                                <span className="inline-flex items-center rounded-md bg-muted/70 px-2 py-1 text-muted-foreground">
                                                                    <span className="mr-1 font-medium text-foreground">Type:</span> {getDisplayValue(secret.type)}
                                                                </span>
                                                                <span className="inline-flex items-center rounded-md bg-muted/70 px-2 py-1 text-muted-foreground">
                                                                    <span className="mr-1 font-medium text-foreground">Immutable:</span> {secret.immutable ? "Yes" : "No"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                            <div className="text-xs text-muted-foreground">Activity</div>
                                                            <ActivityBar count={secret.activity} className="w-20" />
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 pt-3 border-t flex items-center justify-between gap-4">
                                                        <div className="text-left">
                                                            <div className="text-xs text-muted-foreground mb-1.5">Last Activity</div>
                                                            <div className="text-xs font-medium">
                                                                {getDisplayValue(secret.lastActivity || secret.lastActivityMessage || secret.syncedAt, "-")}
                                                            </div>
                                                        </div>
                                                        <div onClick={(e) => e.stopPropagation()}>
                                                            <DetailsDialog data={secret} title={`K8s Secret: ${secret.name}`} />
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    })
                                )}
                            </TabsContent>
                        </Tabs>
                    </div>
                )}
            </div>
        </div>
    );
}
